import React, { useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import cls from 'classnames';

import {
  DailyCampaignDto,
  DailyRewardDto,
  useDailyRewardControllerClaimMutation,
  useLazyDailyRewardControllerGetActiveCampaignStatusQuery,
} from '@/store/services/MidleNestApi.service';
import { useTranslation } from 'react-i18next';
import { Confetti } from '../confetti';
import { useProfile } from '@/hooks';

interface Props {
  campaign?: Partial<DailyCampaignDto>;
  isAvailableToday: boolean;
}

export default function DailyRewardComponent({ campaign, isAvailableToday = false }: Props) {
  const { t } = useTranslation();
  const [claimReward, { isLoading, isSuccess }] = useDailyRewardControllerClaimMutation();
  const [status] = useLazyDailyRewardControllerGetActiveCampaignStatusQuery();
  const { profile, reloadProfile } = useProfile();
  useEffect(() => {
    gtag('event', 'daily-reward-view', { user: profile.id });
  }, []);

  const handleRewardClaim = useCallback(() => {
    if (!campaign || !isAvailableToday || !claimReward) return;
    if (isLoading) return;
    claimReward()
      .then(
        () => {
          status();
          gtag('event', 'daily-reward-claim', { user: profile.id });
        },
        // fetchStatus(campaign.id as string).finally(() => reloadProfile()),
      )
      .finally(() => reloadProfile());
  }, [campaign, isAvailableToday, isLoading, claimReward, reloadProfile, status]);

  // console.log('campaign', { campaign });

  return (
    <>
      <div className='relative flex flex-col items-center'>
        <div className='absolute -right-2 -top-2 md:hidden'>
          <span className='icon-close text-2xl' onClick={() => history.back()} />
        </div>
        <h2 className='text-xl font-bold'>{t('Midle Daily Rewards')}</h2>
        <p className='my-4 italic'>
          {campaign?.title?.en || t('Get a reward every day this month!')}
        </p>
        <p className='mb-4 text-sm text-gray-500'>{campaign?.description?.en}</p>
        <ul className='grid grid-flow-row-dense grid-cols-5 sm:grid-cols-7 text-sm gap-2 text-gray-400'>
          {campaign?.rewards?.map((i: DailyRewardDto, k: number) => {
            const isClaimed = i.status === 'claimed';
            const isAvailable = i.status === 'available';
            const isPassed = i.status === 'passed';
            const nodeCls = cls(
              'relative w-14 h-14 sm:w-20 sm:h-20 p-2 rounded-md flex flex-col items-center justify-center',
              {
                'bg-blue-100 dark:bg-[#FFDA68] dark:text-[#141414]': isClaimed || isAvailable,
                'bg-gray-100 dark:bg-[#FFDA68]/20 text-gray-300 dark:text-white/70': isPassed,
                '!text-blue-500': isClaimed,
              },
            );

            return (
              <li key={k} className={nodeCls}>
                {isClaimed ? (
                  <i className='icon-check h-full text-3xl flex justify-center items-center' />
                ) : (
                  <>
                    <p>{i?.amount}</p>
                    <p>{i?.name}</p>
                  </>
                )}
              </li>
            );
          })}
        </ul>
        <button
          type='button'
          className='mt-4 block px-8 py-3 text-md text-white bg-blue-500 dark:bg-[#FFDA68] dark:text-[#141414] rounded-lg disabled:grayscale disabled:cursor-not-allowed'
          disabled={!isAvailableToday || isLoading}
          onClick={handleRewardClaim}
        >
          {isAvailableToday ? t('Claim') : t('Reward available tomorrow')}
        </button>
      </div>
      {ReactDOM.createPortal(
        <Confetti
          showConfetti={isSuccess}
          balloonCount={10}
          confettiCount={50}
          className='fixed top-0 z-20 pointer-events-none left-0 h-screen w-screen'
        >
          <span />
        </Confetti>,
        document.body,
      )}
    </>
  );
}
